/** Preset label colors (free palette); any `#rrggbb` is accepted too. */
export const PRESET_COLORS = [
  '#61bd4f',
  '#f2d600',
  '#ff9f1a',
  '#eb5a46',
  '#c377e0',
  '#0079bf',
  '#00c2e0',
  '#51e898',
  '#ff78cb',
  '#344563',
] as const;

const HEX = /^#[0-9a-f]{6}$/i;

export function isHexColor(value: string | null | undefined): value is string {
  return typeof value === 'string' && HEX.test(value);
}

/** WCAG relative luminance of a `#rrggbb` color (0 = black, 1 = white). */
export function relativeLuminance(hex: string): number {
  const channel = (offset: number) => {
    const value = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/** WCAG contrast ratio between two `#rrggbb` colors (1..21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** WCAG AA for ordinary text. */
export const AA_NORMAL = 4.5;

export function meetsAA(foreground: string, background: string): boolean {
  return contrastRatio(foreground, background) >= AA_NORMAL;
}

/**
 * Readable text color (black/white) on a `#rrggbb` background: the one with the HIGHER WCAG
 * contrast ratio. (The two curves cross at a luminance of about 0.179, not at 0.4: a threshold
 * above that picks white text on mid-tones where black text is clearly more readable.)
 */
export function contrastText(hex: string): '#000000' | '#ffffff' {
  return contrastRatio(hex, '#000000') >= contrastRatio(hex, '#ffffff') ? '#000000' : '#ffffff';
}

/**
 * The worst (lowest) contrast of `foreground` over several background samples: gradients are
 * checked at their stops and midpoint, patterns at their base, so one weak spot is not hidden
 * by a good average.
 */
export function worstContrast(foreground: string, backgrounds: readonly string[]): number {
  return Math.min(...backgrounds.map((background) => contrastRatio(foreground, background)));
}

/** Black or white, whichever reads best on ALL the sampled backgrounds. */
export function readableOnAll(backgrounds: readonly string[]): '#000000' | '#ffffff' {
  return worstContrast('#000000', backgrounds) >= worstContrast('#ffffff', backgrounds)
    ? '#000000'
    : '#ffffff';
}

/** `rgb(r, g, b)` / `rgba(r, g, b, a)` as computed by the browser -> `#rrggbb` (`null` if opaque color unknown). */
export function cssToHex(value: string): string | null {
  const match = /^rgba?\(\s*(\d{1,3})[ ,]+(\d{1,3})[ ,]+(\d{1,3})(?:[ ,/]+([\d.]+%?))?\s*\)$/.exec(
    value.trim(),
  );
  if (!match) return null;
  const alpha = match[4];
  if (alpha !== undefined && parseFloat(alpha) < 1) return null; // transparent: not a surface
  const hex = [match[1], match[2], match[3]].map((part) =>
    Math.min(255, Number(part)).toString(16).padStart(2, '0'),
  );
  return `#${hex.join('')}`;
}

/** `color` laid over `surface` at `share` opacity (both `#rrggbb`). */
export function blendOver(color: string, surface: string, share: number): string {
  const channel = (hex: string, at: number) => parseInt(hex.slice(at, at + 2), 16);
  const mix = (at: number) =>
    Math.round(channel(color, at) * share + channel(surface, at) * (1 - share))
      .toString(16)
      .padStart(2, '0');
  return `#${mix(1)}${mix(3)}${mix(5)}`;
}
